import axios from 'axios';
import { useEffect, useState } from 'react';
import { ContentCard } from '../../../components/ContentCard/ContentCard';
import { Button, ButtonLink } from '../../../components/Form/Button';
import { Icon, chevron_left, close } from '../../../components/Icon/Icon';
import { Modal } from '../../../components/Modal/Modal';
import { WITH_CREDENTIALS } from '../../../components/utils';
import { useConfig } from '../../../config';
import { useImmer } from '../../../hooks/useImmer';
import { useTranslation } from '../../../i18n/i18n';
import { Res, useApi } from '../../../state/api';
import { useSession } from '../../../state/session';
import { ApiList, ApiSchedule } from '../../../types';
import { calculateNextOrderDate, findNextValidDay, formatCalendarDate, formatScheduleText } from '../utils';
import { EditScheduleForm } from './EditScheduleForm';
import { SelectSuggestions } from './SelectSuggestions';

export const EditScheduleModal = ({ list, deleting }: { list: ApiList; deleting?: boolean }): JSX.Element => {
	const { locale, api } = useConfig();
	const t = { ...useTranslation().subscriptions, generic: useTranslation().generic, urls: useTranslation().routes };
	const { data, mutate } = useApi(Res.schedule, { listId: list.id });

	const [hasSchedule, setHasSchedule] = useState(false);
	const { user } = useSession();
	const startDate = findNextValidDay();

	const DEFAULT_SCHEDULE = {
		id: 0,
		startDate: formatCalendarDate(startDate),
		weeklyDays: '[]',
		monthlyInterval: 'date',
		monthlyDay: 1,
		recurrenceValue: 1,
		productListId: parseInt(list.id),
	} as ApiSchedule;
	const [schedule, setSchedule] = useImmer<ApiSchedule>(DEFAULT_SCHEDULE);
	const [hasDeliveryAddress, setHasDeliveryAddress] = useState(false);
	useEffect(() => {
		if (data?.deliveryAddress && data?.deliveryAddress.id != 0) {
			setHasDeliveryAddress(true);
		} else {
			setHasDeliveryAddress(false);
		}
		if (data) {
			setHasSchedule(true);
			return setSchedule(data);
		}
		setHasSchedule(false);
	}, [data]);

	const [loading, setLoading] = useState(false);
	const [showAdvanced, setShowAdvanced] = useState(schedule !== undefined);
	const [open, setOpen] = useState(false);
	const nextOrderDate = data && calculateNextOrderDate(data);

	const nextOrderDateString = nextOrderDate && formatCalendarDate(nextOrderDate);
	const lastCreatedOrderDateString =
		schedule?.orders?.length > 0 &&
		formatCalendarDate(new Date(schedule?.orders[schedule?.orders.length - 1].dateCreated));
	const scheduleText = data && formatScheduleText(data, locale, t);
	const disabledButton = formatScheduleText(schedule, locale, t) === '';

	const saveSchedule = async () => {
		const payload = {
			...schedule,
			deliveryAddressId: schedule.deliveryAddress?.id,
			customerId: user?.id,
		};

		setLoading(true);

		await axios({
			method: hasSchedule ? 'patch' : 'post',
			url: `${api}me/lists/${list.id}/schedule.json`,
			data: payload,
			withCredentials: true,
		});

		mutate(schedule, true);
		setLoading(false);
		setOpen(false);
	};

	const deleteSchedule = async () => {
		const payload = {
			data: {
				...schedule,
				customerId: user?.id,
			},
			...WITH_CREDENTIALS,
		};

		setLoading(true);

		await axios.delete(`${api}me/lists/${list.id}/schedule.json`, payload);

		setSchedule(DEFAULT_SCHEDULE);
		mutate(schedule, true);
		setLoading(false);
		setOpen(false);
	};

	return (
		<>
			{hasSchedule && hasDeliveryAddress ? (
				<>
					<p className="lead">
						{t.schedule.willBeSentFromUs}: <strong>{scheduleText}</strong>
					</p>
					{nextOrderDate && (
						<p>
							{t.schedule.nextOrder}: <strong>{nextOrderDateString}</strong>
						</p>
					)}
					{schedule?.orders?.length > 0 && (
						<>
							<p>
								<span style={{ marginRight: '8px' }}>
									{t.schedule.lastOrderCreated}: <strong>{lastCreatedOrderDateString}</strong>
								</span>
								<ButtonLink
									className="small"
									linkTo={t.urls.yourOrders + '/' + schedule.orders[schedule.orders.length - 1].order}
								>
									Visa order
								</ButtonLink>
							</p>
						</>
					)}
				</>
			) : (
				<p>{t.selectSchedulingToActivateTheSubscription}</p>
			)}

			<Button
				disabled={loading || deleting}
				style={{ marginBottom: '1.6rem' }}
				onClick={() => {
					setShowAdvanced(schedule.id !== 0);
					setOpen(true);
				}}
			>
				{schedule.id !== 0 ? t.schedule.customizeScheduling : t.schedule.createSchedule}
			</Button>
			{hasSchedule && (
				<Button
					className="secondary"
					disabled={deleting || (loading && !open)}
					style={{ marginBottom: '1.6rem', fontSize: '16px' }}
					onClick={deleteSchedule}
				>
					{loading && !open ? t.schedule.deletingSchedule : t.schedule.deleteSchedule}
				</Button>
			)}

			<Modal
				className="set-subscription-modal"
				show={open}
				onDismiss={() => setOpen(false)}
				toggle={() => setOpen((prev) => !prev)}
			>
				<ContentCard className="set-subscription-content">
					<div className="modal-header">
						{showAdvanced && (
							<button className="close-btn" onClick={() => setShowAdvanced(false)}>
								<Icon shape={{ ...chevron_left, fill: 'black' }} size={20} />
							</button>
						)}
						<div className="modal-header-title">
							{showAdvanced ? t.schedule.customizeScheduling : t.schedule.scheduleSubscription}
						</div>
						<button className="close-btn" onClick={() => setOpen(false)}>
							<Icon shape={{ ...close, fill: 'black' }} size={20} />
						</button>
					</div>
					<div className="modal-body">
						{!showAdvanced ? (
							<SelectSuggestions
								schedule={schedule}
								setShowAdvanced={setShowAdvanced}
								setSchedule={setSchedule}
							/>
						) : (
							<EditScheduleForm schedule={schedule} setSchedule={setSchedule} />
						)}
					</div>
					<div className="modal-footer">
						<Button disabled={loading || disabledButton} onClick={saveSchedule}>
							{loading ? t.generic.saving : t.generic.save}
						</Button>
					</div>
				</ContentCard>
			</Modal>
		</>
	);
};
